import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, Box, Stack } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

interface TimeClockProps {
  onStop?: (start: Date, end: Date, pauseDuration: number, startLat?: number, startLng?: number, endLat?: number, endLng?: number) => void;
}

export const TimeClock: React.FC<TimeClockProps> = ({ onStop }) => {
  const [clockRunning, setClockRunning] = useState(false);
  const [clockStart, setClockStart] = useState<Date | null>(null);
  const [clockNow, setClockNow] = useState<Date>(new Date());
  const [paused, setPaused] = useState(false);
  const [pauseStart, setPauseStart] = useState<Date | null>(null);
  const [totalPause, setTotalPause] = useState(0); // Sekunden

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // LocalStorage laden
  useEffect(() => {
    const storedStart = localStorage.getItem('timetracking_clock_start');
    const storedPause = localStorage.getItem('timetracking_clock_pause');
    const storedTotalPause = localStorage.getItem('timetracking_clock_total_pause');
    if (storedStart) {
      // Prüfe ob es sich um alten UTC-String oder neuen Timestamp handelt
      let startDate;
      if (storedStart.includes('T')) {
        // Alter UTC-String Format - lösche und starte neu
        console.log('Altes UTC-Format erkannt, lösche localStorage');
        localStorage.removeItem('timetracking_clock_start');
        localStorage.removeItem('timetracking_clock_pause');
        localStorage.removeItem('timetracking_clock_total_pause');
        return;
      } else {
        // Neuer Timestamp Format
        startDate = new Date(parseInt(storedStart));
      }
      setClockStart(startDate);
      setClockRunning(true);
    }
    if (storedPause) {
      setPauseStart(new Date(parseInt(storedPause))); // Parse Timestamp
      setPaused(true);
    }
    if (storedTotalPause) {
      setTotalPause(Number(storedTotalPause));
    }
    const interval = setInterval(() => setClockNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Zeitdifferenz als String
  const getElapsed = () => {
    if (!clockStart) return '00:00:00';
    let diff = Math.floor((clockNow.getTime() - clockStart.getTime()) / 1000) - totalPause;
    if (paused && pauseStart) {
      diff -= Math.floor((clockNow.getTime() - pauseStart.getTime()) / 1000);
    }
    if (diff < 0) diff = 0;
    const h = String(Math.floor(diff / 3600)).padStart(2, '0');
    const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
    const s = String(diff % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const getPauseString = () => {
    let pause = totalPause;
    if (paused && pauseStart) {
      pause += Math.floor((clockNow.getTime() - pauseStart.getTime()) / 1000);
    }
    if (pause <= 0) return null;
    const m = String(Math.floor((pause % 3600) / 60)).padStart(2, '0');
    const s = String(pause % 60).padStart(2, '0');
    return `Pause: ${m}:${s}`;
  };

  const handlePause = () => {
    if (!paused) {
      setPaused(true);
      setPauseStart(new Date());
      localStorage.setItem('timetracking_clock_pause', new Date().getTime().toString()); // Speichere Timestamp
    } else if (pauseStart) {
      // Pause beenden
      const pauseSec = Math.floor((new Date().getTime() - pauseStart.getTime()) / 1000);
      setTotalPause(tp => {
        const newTotal = tp + pauseSec;
        localStorage.setItem('timetracking_clock_total_pause', String(newTotal));
        return newTotal;
      });
      setPaused(false);
      setPauseStart(null);
      localStorage.removeItem('timetracking_clock_pause');
    }
  };

  const handleStart = async () => {
    // Request GPS location
    let latitude: number | null = null;
    let longitude: number | null = null;
    
    if (navigator.geolocation) {
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          });
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
        localStorage.setItem('timetracking_start_lat', latitude.toString());
        localStorage.setItem('timetracking_start_lng', longitude.toString());
      } catch (error) {
        console.error('GPS location error:', error);
        // Continue without GPS if user denies or error occurs
      }
    }
    
    const now = new Date();
    setClockStart(now);
    setClockRunning(true);
    setTotalPause(0);
    setPaused(false);
    setPauseStart(null);
    localStorage.setItem('timetracking_clock_start', now.getTime().toString()); // Speichere Timestamp statt ISO String
    localStorage.removeItem('timetracking_clock_pause');
    localStorage.setItem('timetracking_clock_total_pause', '0');
  };

  const handleStop = async () => {
    if (!clockStart) return;
    
    // Get end GPS location
    let endLatitude: number | undefined;
    let endLongitude: number | undefined;
    
    if (navigator.geolocation) {
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          });
        });
        endLatitude = position.coords.latitude;
        endLongitude = position.coords.longitude;
      } catch (error) {
        console.error('GPS location error:', error);
        // Continue without GPS if user denies or error occurs
      }
    }
    
    // Get start GPS from localStorage
    const startLat = localStorage.getItem('timetracking_start_lat');
    const startLng = localStorage.getItem('timetracking_start_lng');
    const startLatitude = startLat ? parseFloat(startLat) : undefined;
    const startLongitude = startLng ? parseFloat(startLng) : undefined;
    
    let pauseSum = totalPause;
    if (paused && pauseStart) {
      pauseSum += Math.floor((new Date().getTime() - pauseStart.getTime()) / 1000);
    }
    setClockRunning(false);
    setPaused(false);
    setPauseStart(null);
    localStorage.removeItem('timetracking_clock_start');
    localStorage.removeItem('timetracking_clock_pause');
    localStorage.removeItem('timetracking_clock_total_pause');
    localStorage.removeItem('timetracking_start_lat');
    localStorage.removeItem('timetracking_start_lng');
    
    if (onStop) {
      onStop(clockStart, new Date(), pauseSum, startLatitude, startLongitude, endLatitude, endLongitude);
    }
    
    setClockStart(null);
    setTotalPause(0);
  };

  return (
    <Card elevation={8} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white', boxShadow: '0 8px 32px rgba(25, 118, 210, 0.15)' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', p: isMobile ? 1.5 : 3 }}>
        {/* Oberer flex-Bereich */}
        <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" width="100%" mb={isMobile ? 2 : 4}>
          <AccessTimeIcon sx={{ fontSize: isMobile ? 56 : 72, color: 'white', mb: 1, filter: 'drop-shadow(0 2px 8px rgba(25,118,210,0.3))' }} />
          <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 700, color: 'white', textAlign: 'center', letterSpacing: 2, textShadow: '0 2px 8px rgba(25,118,210,0.15)' }}>
            {getElapsed()}
          </Typography>
          <Typography variant="subtitle2" sx={{ color: 'white', opacity: 0.85, textAlign: 'center', mt: 0.5, fontSize: isMobile ? 14 : 16 }}>
            Stempeluhr
          </Typography>
          {getPauseString() && (
            <Typography variant="body2" sx={{ color: 'white', opacity: 0.7, textAlign: 'center', mt: 0.5, fontSize: isMobile ? 13 : 15 }}>
              {getPauseString()}
            </Typography>
          )}
          {clockStart && (
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.6, textAlign: 'center', mt: 0.5 }}>
              Gestartet: {clockStart.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        {/* Unterer Bereich: Buttons immer am unteren Rand */}
        <Box width="100%" display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} justifyContent="center" alignItems="center" mt={isMobile ? 1 : 0} mb={isMobile ? 1 : 2}>
          <Button
            variant="contained"
            color="success"
            onClick={handleStart}
            disabled={clockRunning}
            sx={{
              maxWidth: 220,
              width: isMobile ? '100%' : 'auto',
              fontWeight: 'bold',
              fontSize: isMobile ? 17 : 18,
              py: isMobile ? 1 : 1.2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(76,175,80,0.10)',
              transition: 'transform 0.15s',
              '&:hover': { transform: 'scale(1.04)' },
              opacity: clockRunning ? 0.5 : 1,
            }}
            fullWidth={isMobile}
          >
            Start
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handlePause}
            disabled={!clockRunning}
            sx={{
              maxWidth: 220,
              width: isMobile ? '100%' : 'auto',
              fontWeight: 'bold',
              fontSize: isMobile ? 17 : 18,
              py: isMobile ? 1 : 1.2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(255,193,7,0.08)',
              transition: 'transform 0.15s',
              '&:hover': { transform: 'scale(1.04)' },
              opacity: !clockRunning ? 0.5 : 1,
            }}
            fullWidth={isMobile}
          >
            {paused ? 'Weiter' : 'Pause'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleStop}
            disabled={!clockRunning}
            sx={{
              maxWidth: 220,
              width: isMobile ? '100%' : 'auto',
              fontWeight: 'bold',
              fontSize: isMobile ? 17 : 18,
              py: isMobile ? 1 : 1.2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(244,67,54,0.08)',
              transition: 'transform 0.15s',
              '&:hover': { transform: 'scale(1.04)' },
              opacity: !clockRunning ? 0.5 : 1,
            }}
            fullWidth={isMobile}
          >
            Stop
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}; 